// AI-ASSISTED: generated with claude-code on 2026-06-29, reviewed by Yoann Hertienne
import { useMemo } from 'react';
import useAsync from 'react-use/lib/useAsync';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { TileBoundary } from '../team-landing/TileBoundary';

// The component is mapped to its scanned container image via the ACR repo it
// already declares for the ACR card — no dedicated Tenable annotation. The
// full repository URI Tenable Cloud Security filters on is the registry host
// joined to that repo; the digest-specific image id changes every build and
// is resolved at query time.
const ACR_REPO_ANNOTATION = 'azure.com/acr-repo';

type Env = 'NPRD' | 'PRD';

// The two ACR login servers this Tenable tenant scans (mirror
// ACR_NPRD_BASE_URL / ACR_PRD_BASE_URL). The card probes BOTH so a component's
// non-prod and prod images are both covered.
const ACR_REGISTRY_HOSTS: Record<Env, string> = {
  NPRD: 'infracoreacrweunonprdacr.azurecr.io',
  PRD: 'infracoreacrweuprdacr.azurecr.io',
};

// Per-env candidate repo-name suffixes, mirroring modules/acr-card so the two
// tiles resolve the same images. Bare `<repo>` covers components whose image
// name is identical across envs (e.g. backstage); the `-b2c-euw-<env>`
// variants cover components that split images per env (e.g. my-luminus-web).
const ENV_SUFFIXES: Record<Env, string[]> = {
  NPRD: ['', '-b2c-euw-dev', '-b2c-euw-tst', '-b2c-euw-acc'],
  PRD: ['', '-b2c-euw-prd'],
};

// Tenable Cloud Security (Ermetic) EU console. No documented per-resource
// deep-link format, so we link to the console root (truthful, always valid).
const TENABLE_UI_BASE = 'https://eu.app.ermetic.com';
// Cap pagination so a pathological image set cannot spin the tile forever.
const MAX_PAGES = 5;
const PAGE_SIZE = 200;

type Severity = 'Critical' | 'High';

/** A single Critical/High vulnerability instance on a resolved image. */
export type Finding = {
  /** CVE / advisory id, e.g. `cve-2025-15467`. */
  id: string;
  severity: Severity;
  cvssScore: number | null;
  softwareName: string;
  softwareVersion: string;
  /** Tenable flags whether a public exploit is known. */
  exploitable: boolean;
  /** Which registry the affected image lives in. */
  env: Env;
};

type Result = {
  /** False when no candidate URI matched a scanned image in either registry. */
  imageFound: boolean;
  findings: Finding[];
};

const CONTAINER_IMAGES_QUERY = `query($f: ContainerImagesFilterInput, $n: Int, $after: String) {
  ContainerImages(first: $n, after: $after, filter: $f) {
    pageInfo { hasNextPage endCursor }
    nodes { Id ScanTime RepositoryUri }
  }
}`;

const VULN_INSTANCES_QUERY = `query($f: VulnerabilityInstancesFilterInput, $n: Int, $after: String) {
  VulnerabilityInstances(first: $n, after: $after, filter: $f) {
    pageInfo { hasNextPage endCursor }
    nodes {
      Software { Name Version }
      Vulnerability { Id CvssScore CvssSeverity Exploitable }
      Resource { Id }
    }
  }
}`;

/** Classifies a repository URI or resource id by which registry host it carries. */
export const envForUri = (ref: string | undefined | null): Env =>
  (ref ?? '').includes(ACR_REGISTRY_HOSTS.PRD) ? 'PRD' : 'NPRD';

/**
 * Derives the candidate Tenable Cloud Security repository URIs from the
 * component's `azure.com/acr-repo` annotation, across BOTH registries and the
 * per-env name variants the ACR card probes. The annotation normally holds
 * just the repo name (e.g. `backstage`); a value that already carries a
 * registry host (`*.azurecr.io/...`) is trusted as-is. Returns `[]` when the
 * annotation is absent.
 */
export const deriveRepositoryUris = (
  acrRepo: string | undefined,
): Array<{ env: Env; uri: string }> => {
  const repo = acrRepo?.trim().replace(/^https?:\/\//, '').replace(/^\/+|\/+$/g, '');
  if (!repo) return [];
  if (repo.includes('.azurecr.io/')) return [{ env: envForUri(repo), uri: repo }];
  const out: Array<{ env: Env; uri: string }> = [];
  (Object.keys(ENV_SUFFIXES) as Env[]).forEach(env => {
    ENV_SUFFIXES[env].forEach(suffix => {
      out.push({ env, uri: `${ACR_REGISTRY_HOSTS[env]}/${repo}${suffix}` });
    });
  });
  return out;
};

type ImageNode = { Id: string; ScanTime?: string | null; RepositoryUri?: string | null };

/**
 * Groups container-image nodes by repository URI and returns the latest
 * scanned image id for each — so the card reports one current image per
 * matched registry/repo rather than every historical digest.
 */
export const pickLatestPerRepository = (nodes: ReadonlyArray<ImageNode>): string[] => {
  const latest = new Map<string, ImageNode>();
  for (const node of nodes) {
    if (!node?.Id) continue;
    const key = node.RepositoryUri ?? node.Id;
    const current = latest.get(key);
    if (!current || (node.ScanTime ?? '') > (current.ScanTime ?? '')) {
      latest.set(key, node);
    }
  }
  return [...latest.values()].map(node => node.Id);
};

type RawInstance = {
  Software?: { Name?: string | null; Version?: string | null } | null;
  Vulnerability?: {
    Id?: string | null;
    CvssScore?: number | null;
    CvssSeverity?: string | null;
    Exploitable?: boolean | null;
  } | null;
  Resource?: { Id?: string | null } | null;
};

type ContainerImagesData = {
  ContainerImages: {
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
    nodes: ImageNode[];
  };
};

type VulnInstancesData = {
  VulnerabilityInstances: {
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
    nodes: RawInstance[];
  };
};

/** Maps a raw GraphQL VulnerabilityInstance node into a {@link Finding}. */
export const toFinding = (node: RawInstance): Finding => ({
  id: node.Vulnerability?.Id ?? 'unknown',
  severity: node.Vulnerability?.CvssSeverity === 'Critical' ? 'Critical' : 'High',
  cvssScore:
    typeof node.Vulnerability?.CvssScore === 'number'
      ? node.Vulnerability.CvssScore
      : null,
  softwareName: node.Software?.Name ?? 'unknown',
  softwareVersion: node.Software?.Version ?? '',
  exploitable: node.Vulnerability?.Exploitable === true,
  env: envForUri(node.Resource?.Id),
});

/** Counts findings by severity for the summary chips. */
export const countBySeverity = (findings: ReadonlyArray<Finding>) => ({
  critical: findings.filter(f => f.severity === 'Critical').length,
  high: findings.filter(f => f.severity === 'High').length,
});

/** Renders the no-image notice, the empty-findings notice, or the list. */
const FindingsSection = ({
  imageFound,
  findings,
}: {
  imageFound: boolean;
  findings: ReadonlyArray<Finding>;
}) => {
  if (!imageFound) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
        No scanned image found in either registry.
      </Typography>
    );
  }
  if (findings.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
        No Critical or High findings.
      </Typography>
    );
  }
  return (
    <List
      dense
      aria-label="Critical and High Tenable findings"
      sx={{ maxHeight: 260, overflow: 'auto' }}
    >
      {findings.map((finding, index) => (
        <ListItem key={`${finding.id}-${finding.softwareName}-${finding.env}-${index}`} disableGutters>
          <Chip
            size="small"
            color={finding.severity === 'Critical' ? 'error' : 'warning'}
            label={finding.severity}
            sx={{ mr: 1, minWidth: 64 }}
          />
          <Chip size="small" variant="outlined" label={finding.env} sx={{ mr: 1 }} />
          <ListItemText
            primary={`${finding.id}${
              finding.cvssScore !== null ? ` (CVSS ${finding.cvssScore})` : ''
            }`}
            secondary={`${finding.softwareName} ${finding.softwareVersion}${
              finding.exploitable ? ' · exploitable' : ''
            }`}
          />
        </ListItem>
      ))}
    </List>
  );
};

const TenableCardBody = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const acrRepo = entity.metadata.annotations?.[ACR_REPO_ANNOTATION];
  const candidates = useMemo(() => deriveRepositoryUris(acrRepo), [acrRepo]);

  const { value, loading, error } = useAsync(async (): Promise<Result | null> => {
    if (candidates.length === 0) return null;
    const baseUrl = await discoveryApi.getBaseUrl('proxy');
    const endpoint = `${baseUrl}/tenable/api/graph`;

    const graphql = async (
      query: string,
      variables: Record<string, unknown>,
    ): Promise<unknown> => {
      const response = await fetchApi.fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, variables }),
      });
      if (!response.ok) {
        throw new Error(`Tenable request failed: ${response.status}`);
      }
      const json = (await response.json()) as {
        data?: unknown;
        errors?: Array<{ message?: string }>;
      };
      if (json.errors?.length) {
        throw new Error(`Tenable GraphQL error: ${json.errors[0]?.message}`);
      }
      return json.data;
    };

    // 1. Resolve the candidate URIs (both registries) to their scanned images,
    //    paging so the latest-per-repo pick sees every digest.
    const imageNodes: ImageNode[] = [];
    let imageCursor: string | null = null;
    for (let page = 0; page < MAX_PAGES; page++) {
      const data = (await graphql(CONTAINER_IMAGES_QUERY, {
        n: PAGE_SIZE,
        after: imageCursor,
        f: { RepositoryUris: candidates.map(candidate => candidate.uri) },
      })) as ContainerImagesData;
      const connection = data.ContainerImages;
      imageNodes.push(...connection.nodes);
      if (!connection.pageInfo.hasNextPage) break;
      imageCursor = connection.pageInfo.endCursor;
    }
    const imageIds = pickLatestPerRepository(imageNodes);
    if (imageIds.length === 0) return { imageFound: false, findings: [] };

    // 2. Page through Critical/High, unresolved instances across those images.
    //    The connection exposes no totalCount, so we accumulate node pages.
    const findings: Finding[] = [];
    let after: string | null = null;
    for (let page = 0; page < MAX_PAGES; page++) {
      const data = (await graphql(VULN_INSTANCES_QUERY, {
        n: PAGE_SIZE,
        after,
        f: {
          ResourceIds: imageIds,
          CvssSeverities: ['Critical', 'High'],
          Resolved: false,
        },
      })) as VulnInstancesData;
      const connection = data.VulnerabilityInstances;
      findings.push(...connection.nodes.map(toFinding));
      if (!connection.pageInfo.hasNextPage) break;
      after = connection.pageInfo.endCursor;
    }
    return { imageFound: true, findings };
  }, [candidates]);

  const findings = useMemo(
    () =>
      [...(value?.findings ?? [])].sort(
        (a, b) => (b.cvssScore ?? 0) - (a.cvssScore ?? 0),
      ),
    [value],
  );
  const counts = countBySeverity(findings);

  if (candidates.length === 0) return null;

  if (error) {
    // Re-throw so the TileBoundary above renders the degraded state.
    throw error;
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6">Tenable</Typography>
        {loading ? (
          <Box sx={{ py: 1 }}>
            <CircularProgress size={20} aria-label="Loading Tenable findings" />
          </Box>
        ) : (
          <>
            <Stack direction="row" spacing={1} sx={{ pt: 1 }} flexWrap="wrap">
              <Chip size="small" color="error" label={`Critical: ${counts.critical}`} />
              <Chip size="small" color="warning" label={`High: ${counts.high}`} />
            </Stack>
            <FindingsSection imageFound={value?.imageFound !== false} findings={findings} />
          </>
        )}
      </CardContent>
      <CardActions>
        <Button
          variant="outlined"
          size="small"
          href={TENABLE_UI_BASE}
          target="_blank"
          rel="noreferrer"
          aria-label="Open Tenable Cloud Security console"
        >
          Open in Tenable
        </Button>
      </CardActions>
    </Card>
  );
};

export const TenableCard = () => (
  <TileBoundary tool="Tenable">
    <TenableCardBody />
  </TileBoundary>
);
